import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Loader2 } from 'lucide-react';

const AuthCallback = () => {
    const { processSession } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const hasProcessed = useRef(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Use ref to prevent double processing in StrictMode
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const handleCallback = async () => {
            try {
                // Get session_id from URL hash
                const hash = location.hash;
                const params = new URLSearchParams(hash.replace('#', ''));
                const sessionId = params.get('session_id');

                if (!sessionId) {
                    setError('No session ID found');
                    setTimeout(() => navigate('/'), 2000);
                    return;
                }

                // Process the session
                const user = await processSession(sessionId);
                
                // Clear the URL hash
                window.history.replaceState(null, '', window.location.pathname);
                
                // Check if user was in Multi-Apply flow
                const multiApplyStep = localStorage.getItem('multiApplyStep');
                const multiApplyFormData = localStorage.getItem('multiApplyFormData');
                
                // Check if user had a pricing intent
                const pricingIntent = localStorage.getItem('pricingIntent');
                
                // Check for general login return URL
                const loginReturnUrl = localStorage.getItem('loginReturnUrl');
                
                if (multiApplyStep && multiApplyFormData) {
                    // User came from Multi-Apply, redirect back there
                    navigate('/multi-apply', { state: { user, returnFromAuth: true }, replace: true });
                } else if (pricingIntent) {
                    // User came from Pricing page, redirect back there
                    localStorage.removeItem('loginReturnUrl');
                    navigate('/pricing', { state: { user, returnFromAuth: true }, replace: true });
                } else if (loginReturnUrl) {
                    // User came from login page with specific return URL
                    localStorage.removeItem('loginReturnUrl');
                    navigate(loginReturnUrl, { state: { user }, replace: true });
                } else {
                    // Navigate to dashboard with user data
                    navigate('/dashboard', { state: { user }, replace: true });
                }
            } catch (err) {
                console.error('Auth callback error:', err);
                setError('Failed to authenticate. Please try again.');
                setTimeout(() => navigate('/'), 2000);
            }
        };

        handleCallback();
    }, [location.hash, processSession, navigate]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-neutral-50">
            <div className="text-center">
                {error ? (
                    <div className="text-red-600">
                        <p className="text-lg font-medium">{error}</p>
                        <p className="text-sm text-neutral-500 mt-2">Redirecting...</p>
                    </div>
                ) : (
                    <>
                        <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
                        <p className="text-lg font-medium text-neutral-900">Signing you in...</p>
                        <p className="text-sm text-neutral-500 mt-1">Please wait</p>
                    </>
                )}
            </div>
        </div>
    );
};

export default AuthCallback;
